import { Component, computed, Signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from 'src/app/services/app.service';
import { iAppInfo } from 'src/app/interfaces/app-info';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import { CacheService } from 'src/app/services/cache.service';
import { IonicModule, isPlatform } from '@ionic/angular';
import { iUser } from 'src/app/interfaces/user';

@Component({
    selector: 'page-login',
    templateUrl: 'login.page.html',
    styleUrls: ['./login.page.scss'],
    standalone: true,
    imports: [
        IonicModule,
        FormsModule,
        ReactiveFormsModule,
    ],
})
export class LoginPage {

    public appInfo: iAppInfo = this.app.appInfo;
    public appVersion: string = environment.app_version;
    public isLoading: boolean = true;
    public isSubmitted: boolean = false;
    public customEndpoint: boolean = false;
    public user: Signal<iUser> = computed(() => this.authService.user());
    public isGRB: Signal<boolean> = computed(() => this.app.isGRB());

    public loginForm = new UntypedFormGroup({
        username: new UntypedFormControl('', Validators.required),
        password: new UntypedFormControl('', Validators.required)
    });

    constructor(
        public router: Router,
        private toast: ToastService,
        private app: AppService,
        private authService: AuthenticationService,
        private cache: CacheService
    ) { }

    ionViewWillEnter() {
        this.isSubmitted = false;
        this.customEndpoint = false;

        // Try login if user is stored
        this.redirectUser();
    }

    ionViewDidEnter() {
        // A small aesthetic delay to hide login form on token login
        setTimeout(() => {
            this.isLoading = false;
        }, 1000);
    }

    /**
     * User login form validation and redirection
     * 
     * @param form 
     */
    public login() {
        this.isSubmitted = true;

        // Set devmode to false
        // This is needed in case a dev does a re-login without restarting the app
        this.app.isDev.set(false);

        // If we have a custom endpoint we need to set it
        if (this.loginForm.controls['password'].value === 'setapi' && !this.loginForm.controls['username'].value) {
            this.loginForm.addControl('endpoint', new UntypedFormControl('', Validators.required));
            this.customEndpoint = true;
            this.loginForm.reset();
            return;
        }

        // If form is valid we can login
        if (this.loginForm.valid) {
            this.isLoading = true;

            // Reset the endpoint if we have a custom endpoint
            if (this.app.apiEndpointOverride !== '') {
                this.app.apiEndpointOverride = '';
            }

            // If we have a custom endpoint we save the old endpoint and overwrite the environment variable
            if (this.loginForm.controls['endpoint'] && this.loginForm.controls['endpoint'].value !== '') {
                this.app.setApiEndpointOverride(this.loginForm.controls['endpoint'].value);
            }

            // Overwrite api-endpoint for "test-" prefixed users
            if (this.loginForm.controls['username'].value.startsWith('test-')) {
                this.app.setApiEndpointOverride(environment.api_test_endpoint, false);
            }

            // Login the user
            this.authService.login(this.loginForm.controls['username'].value, this.loginForm.controls['password'].value).subscribe({
                next: () => {
                    // Clear old cache to prevent directs to the old data-points
                    this.cache.clearCache();

                    // Reset form
                    this.loginForm.removeControl('endpoint');
                    this.loginForm.reset();

                    // Redirect to the main page for the current user
                    this.redirectUser();
                },
                error: (error) => {
                    this.isLoading = false;
                    if (error.error.error === 'invalid_grant') {
                        this.toast.presentToast('Forkert brugernavn eller adgangskode', 'warning');
                    } else {
                        this.toast.presentToast(error.message, 'warning');
                    }
                }
            });
        }
    }

    /**
     * Alert to placehold the missing request password feature
     */
    public async requestPassword() {
        if (this.isGRB) {
            this.app.popMessage('Glemt adgangskode', 'Kontakt Greenland Travel for nulstillings af adgangskode.');
        } else {
            this.app.popMessage('Glemt adgangskode', 'Kontakt Arctic Excursions for support på tlf. ' + this.appInfo.contact_phone);
        }
    }

    /**
     * Redirect the user to the correct main page based on the user role
     */
    private redirectUser() {
        if(this.authService.isLoggedIn()) {
            try {
                // Hent brugerroller
                const userRoles = this.authService.getUserRoles();
                if (!userRoles || !Array.isArray(userRoles)) {
                    throw new Error('Kunne ikke hente brugerroller');
                }

                // Tjek om det er i appen der logges ind
                const appPlatform = isPlatform('ios') || isPlatform('android');

                // Håndter forskellige brugertyper
                if (userRoles.includes('tour_operator') || appPlatform) {
                    this.router.navigate(['/tour-operator']);
                } else {
                    this.router.navigate(['/tour-consultant/booking']);
                }
            } catch (error) {
                this.toast.presentToast('Der skete en fejl ved omdirigering', 'danger');
                this.isLoading = false;
                this.authService.killSession();
            }
        }
        else {
            // Silent fail if nothing is going on
        }
    }
}